'use client';

import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Check from 'lucide-react/dist/esm/icons/check';
import X from 'lucide-react/dist/esm/icons/x';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';

const DataTable = forwardRef(
  ({ columns, data, onRowClick, onRowUpdate, vehicleTypes = [], locations = [] }, ref) => {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [columnVisibility, setColumnVisibility] = useState({});
    const [rowSelection, setRowSelection] = useState({});
    const [editingRow, setEditingRow] = useState(null);
    const [editData, setEditData] = useState({});

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      onSortingChange: setSorting,
      getSortedRowModel: getSortedRowModel(),
      onColumnFiltersChange: setColumnFilters,
      getFilteredRowModel: getFilteredRowModel(),
      onColumnVisibilityChange: setColumnVisibility,
      onRowSelectionChange: setRowSelection,
      state: {
        sorting,
        columnFilters,
        columnVisibility,
        rowSelection,
      },
    });

    const handleSaveEdit = () => {
      if (onRowUpdate && editingRow) {
        const rowData = data.find((row) => row.id === editingRow);
        onRowUpdate(rowData, editData);
      }
      setEditingRow(null);
      setEditData({});
    };

    const handleCancelEdit = () => {
      setEditingRow(null);
      setEditData({});
    };

    const handleInputChange = (columnId, value) => {
      setEditData((prev) => ({ ...prev, [columnId]: value }));
    };

    const renderEditableCell = useCallback(
      (cell, row) => {
        const columnId = cell.column.id;
        const currentValue =
          editData[columnId] !== undefined ? editData[columnId] : cell.getValue();

        if (columnId === 'actions') {
          return (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSaveEdit}
                className="h-6 w-6 p-0 cursor-pointer bg-green-500 hover:bg-green-600"
              >
                <Check className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancelEdit}
                className="h-6 w-6 p-0 cursor-pointer hover:bg-gray-100"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          );
        }

        switch (columnId) {
          case 'vehicleType':
            return (
              <Select
                value={currentValue}
                onValueChange={(value) => handleInputChange(columnId, value)}
              >
                <SelectTrigger className="h-8 cursor-pointer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {vehicleTypes.map((type) => (
                    <SelectItem
                      key={type.value}
                      value={type.value}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );

          case 'location':
            return (
              <Select
                value={currentValue}
                onValueChange={(value) => handleInputChange(columnId, value)}
              >
                <SelectTrigger className="h-8 cursor-pointer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {locations.map((location) => (
                    <SelectItem
                      key={location}
                      value={location}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );

          case 'charges':
            return (
              <Input
                type="number"
                value={currentValue}
                onChange={(e) => handleInputChange(columnId, e.target.value)}
                className="h-8 cursor-text"
              />
            );

          default:
            return (
              <Input
                value={currentValue}
                onChange={(e) => handleInputChange(columnId, e.target.value)}
                className="h-8 cursor-text"
              />
            );
        }
      },
      [editData, vehicleTypes, locations],
    );

    useImperativeHandle(ref, () => ({
      setEditingRow: (rowId, rowData) => {
        setEditingRow(rowId);
        setEditData(rowData);
      },
    }));

    return (
      <div className="w-full" style={{ isolation: 'isolate', maxWidth: '100%' }}>
        <div className="rounded-md">
          <div
            className="border border-[#e8e8e8] rounded-[10px]"
            style={{
              width: '100%',
              maxWidth: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              className="overflow-x-auto"
              style={{
                width: '100%',
                maxWidth: '100%',
                position: 'relative',
              }}
            >
              <table
                className="text-sm border-collapse"
                style={{
                  width: '100%',
                  minWidth: 'max-content',
                }}
              >
                <thead className="bg-[color:var(--brand-primary)] text-white">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="h-[56px]">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={cn(
                            'px-4 py-3 font-semibold text-[14px] font-[600] font-poppins',
                            header.column.id === 'actions' ? 'text-right pr-6' : 'text-left',
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length ? (
                    table.getRowModel().rows.map((row, rowIndex) => (
                      <tr
                        key={row.id}
                        className={cn(
                          'border-b-[0px] transition-colors',
                          editingRow === row.original.id
                            ? 'bg-blue-50 border-blue-200'
                            : 'hover:bg-gray-100',
                          rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                        )}
                        onClick={() => editingRow !== row.original.id && onRowClick?.(row.original)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="text-[16px] font-normal font-poppins px-4 py-4 max-w-[300px]"
                          >
                            {editingRow === row.original.id
                              ? renderEditableCell(cell, row)
                              : flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="h-24 text-center">
                        No results.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

DataTable.displayName = 'DataTable';

export default DataTable;
